// Pure helpers for the `draw` op's two dynamic capabilities:
//   1. count. Draw N where N is the size of a resolved in-play scope
//      (mirrors grant_stat_per_each_self), optionally collapsed to the
//      number of DISTINCT values of a field (distinct_by).
//   2. from. 'top' (default) or 'bottom' of the pile.
//
// No runtime dependencies: the caller resolves the scope via
// host._resolveAuraScope and passes the match list in; the actual draw
// routes through host.drawCard. This module only does the arithmetic
// and the bottom-card pick.

import { matchesFilter } from './cardFilter';

/**
 * Number of distinct values of `field` across `matches`. Reads the
 * live instance field first, then falls back to the catalog row via
 * getFullCard so card-shape fields absent on the instance (rarity) are
 * still distinguishable. Cards with no value collapse under a single
 * empty-string bucket.
 */
export function distinctCount(matches, field, getFullCard = null) {
  const seen = new Set();
  for (const card of matches) {
    let value = card?.[field];
    if (value == null && getFullCard && card?.cardId) {
      value = getFullCard(card.cardId)?.[field];
    }
    seen.add(String(value ?? '').toLowerCase());
  }
  return seen.size;
}

/**
 * Resolve how many cards a `draw` op pulls. `args.count` (already
 * resolved to a `matches` array by the caller) takes precedence and is
 * clamped at >= 0; `distinct_by` counts distinct field values instead
 * of raw matches. Falls back to `args.amount` (default 1) when no
 * dynamic count is requested.
 */
export function resolveDrawCount(matches, args = {}, getFullCard = null) {
  if (!args.count) return args.amount ?? 1;
  const field = args.count.distinct_by;
  const n = field ? distinctCount(matches, field, getFullCard) : matches.length;
  return Math.max(0, n);
}

/**
 * S1. Codex Draw: "you may choose to draw a spell from your spellbook or a
 * site from your atlas." A `source:'card'` draw offers the drawer that pick.
 * Returns the pile names the drawer can legally pull from right now:
 * spellbook needs cards AND a remaining spell-draw allowance (Garden of Eden
 * chokes spell draws only), atlas just needs cards. Empty result = no draw.
 *
 * @param {{ spellbookCount: number, atlasCount: number, spellDrawsRemaining?: number }} p
 * @returns {('spellbook'|'atlas')[]}
 */
export function drawSourceOptions({ spellbookCount, atlasCount, spellDrawsRemaining = Infinity }) {
  const options = [];
  if (spellbookCount > 0 && spellDrawsRemaining > 0) options.push('spellbook');
  if (atlasCount > 0) options.push('atlas');
  return options;
}

/** Bottom card id of a pile, or null when empty. Top is the last element. */
export function bottomCardId(pile) {
  const cards = pile?.cards;
  return cards && cards.length > 0 ? cards[0].id : null;
}

/**
 * Filter a resolved scope down to the entries the count's filter
 * matches. Scope resolution already applies the filter, so this is the
 * identity in the runtime path; exposed so tests can drive the
 * arithmetic with a raw card list + ctx.
 */
export function matchesForCount(cards, countArgs = {}, ctx = {}) {
  const filter = countArgs.filter || {};
  return cards.filter((card) => {
    if (countArgs.exclude_self && ctx.source?.id && card.id === ctx.source.id) return false;
    return matchesFilter(card, filter, ctx);
  });
}
